// Atendimento do site (Dom, A22): conversas, pré-qualificação e retorno em 1 clique pela equipe comercial.
import { useState } from 'react';
import { fmtDate } from '../api';
import { Badge, Card, Empty, ErrorBox, PageHeader, useApi } from '../components/ui';

interface Session {
  id: string;
  status: string;
  collected: Record<string, string | null>;
  qualification: { technicalFit: string; commercialFit: string; summary: string } | null;
  handoff: string | null;
  leadId: string | null;
  crmOpportunityId: string | null;
  whatsappStatus: string | null;
  userMessages: number;
  createdAt: string;
  lastMessageAt: string | null;
  pageUrl: string | null;
}

const STATUS: Record<string, ['green' | 'amber' | 'slate' | 'red', string]> = {
  ABERTA: ['amber', 'Em conversa'],
  ENCAMINHADA: ['green', 'Encaminhada ao comercial'],
  ENCERRADA: ['slate', 'Encerrada'],
  BLOQUEADA: ['red', 'Bloqueada'],
};
const FIT: Record<string, 'green' | 'amber' | 'slate' | 'red'> = { alto: 'green', medio: 'amber', baixo: 'red', indefinido: 'slate' };
const WA: Record<string, string> = { ENVIADO: 'WhatsApp enviado pelo WeSales', SEM_PERMISSAO: 'WhatsApp pelo WeSales sem permissão: chamar pelo botão', FALHOU: 'WhatsApp pelo WeSales falhou: chamar pelo botão', NAO_CONFIGURADO: 'CRM não configurado' };

function Transcript({ id }: { id: string }) {
  const t = useApi<{ role: string; text: string; createdAt: string }[]>(`/api/site-chat/${id}`);
  return (
    <ul className="mt-2 max-h-80 space-y-1 overflow-y-auto">
      {t.data?.map((m, i) => (
        <li key={i} className={`rounded p-2 text-sm ${m.role === 'dom' ? 'bg-slate-50' : 'bg-brand-50'}`}>
          <span className="text-xs text-slate-500">
            {m.role === 'dom' ? 'Dom' : 'Visitante'} · {fmtDate(m.createdAt)}
          </span>
          <p className="whitespace-pre-line">{m.text}</p>
        </li>
      ))}
    </ul>
  );
}

export function SiteChatAdmin() {
  const list = useApi<Session[]>('/api/site-chat');
  const [open, setOpen] = useState<string | null>(null);

  return (
    <div className="space-y-4">
      <PageHeader
        title="Atendimento do site — Dom (A22)"
        subtitle="Conversas do chat do site com o Dom (assistente com IA). Com contato e consentimento, o lead entra no sistema, a oportunidade é criada no WeSales e o comercial recebe o alerta para ligar ou chamar no WhatsApp."
      />
      <Card title="Instalação no site">
        <p className="text-sm text-slate-700">O botão "Fale com o Dom" aparece no site com esta linha no fim das páginas:</p>
        <code className="mt-1 block rounded bg-slate-100 p-2 text-xs">{'<script src="https://dpmkt.3snetworks.com.br/widget/chat.js" async></script>'}</code>
      </Card>
      <ErrorBox error={list.error} />
      {list.data?.length ? (
        <ul className="space-y-3">
          {list.data.map((s) => {
            const c = s.collected ?? {};
            const digits = (c.phone ?? '').replace(/\D/g, '');
            return (
              <li key={s.id} className="rounded-lg border border-slate-200 bg-white p-4">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <p className="font-semibold text-slate-900">
                      {c.name ?? 'Visitante sem nome'} <span className="font-normal text-slate-600">· {c.role ?? 'cargo não informado'} · {c.company ?? 'empresa não informada'}</span>
                    </p>
                    <p className="text-xs text-slate-500">
                      {fmtDate(s.lastMessageAt ?? s.createdAt)} · {s.userMessages} mensagens{c.email ? ` · ${c.email}` : ''}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge tone={STATUS[s.status]?.[0] ?? 'slate'}>{STATUS[s.status]?.[1] ?? s.status}</Badge>
                    {s.qualification && (
                      <>
                        <Badge tone={FIT[s.qualification.technicalFit] ?? 'slate'}>técnico: {s.qualification.technicalFit}</Badge>
                        <Badge tone={FIT[s.qualification.commercialFit] ?? 'slate'}>comercial: {s.qualification.commercialFit}</Badge>
                      </>
                    )}
                  </div>
                </div>
                <p className="mt-2 text-sm text-slate-700">
                  <strong>Solução:</strong> {c.solution ?? '—'} · <strong>Necessidade:</strong> {c.need ?? '—'} · <strong>Sites/usuários:</strong> {c.sites ?? '—'}/{c.users ?? '—'} · <strong>Prazo:</strong> {c.timeline ?? '—'}
                </p>
                {s.qualification?.summary && <p className="text-sm text-slate-600">{s.qualification.summary}</p>}
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  {c.phone && (
                    <>
                      <a href={`tel:${c.phone}`} className="rounded bg-brand-900 px-3 py-2 text-sm font-semibold text-white">
                        Ligar {c.phone}
                      </a>
                      <a href={`https://wa.me/${digits}`} target="_blank" rel="noopener noreferrer" className="rounded bg-[#128c7e] px-3 py-2 text-sm font-semibold text-white">
                        WhatsApp ↗
                      </a>
                    </>
                  )}
                  {c.email && (
                    <a href={`mailto:${c.email}`} className="rounded border border-slate-300 px-3 py-2 text-sm">
                      E-mail
                    </a>
                  )}
                  {s.handoff && <Badge tone="blue">pediu: {s.handoff}</Badge>}
                  {s.crmOpportunityId && <Badge tone="green">oportunidade no WeSales</Badge>}
                  {s.whatsappStatus && <Badge tone={s.whatsappStatus === 'ENVIADO' ? 'green' : 'amber'}>{WA[s.whatsappStatus] ?? s.whatsappStatus}</Badge>}
                  <button className="text-sm text-brand-700 underline" onClick={() => setOpen(open === s.id ? null : s.id)}>
                    {open === s.id ? 'Ocultar conversa' : 'Ver conversa'}
                  </button>
                </div>
                {open === s.id && <Transcript id={s.id} />}
              </li>
            );
          })}
        </ul>
      ) : (
        !list.loading && <Empty>Nenhuma conversa ainda. Depois de instalar o widget no site, as conversas aparecem aqui.</Empty>
      )}
    </div>
  );
}
